import React from "react";
import Icon from "../../../components/AppIcon";

const ProcessSection = () => {
  const processSteps = [
    {
      step: "01",
      title: "Discovery & Strategy",
      description:
        "We dive deep into your vision, audience, and objectives to craft a tailored approach that aligns with your brand story.",
      icon: "Search",
      duration: "1-2 weeks",
    },
    {
      step: "02",
      title: "Creative Development",
      description:
        "Our team develops concepts, storyboards, and visual treatments that bring your ideas to life with cinematic excellence.",
      icon: "Lightbulb",
      duration: "2-3 weeks",
    },
    {
      step: "03",
      title: "Production Excellence",
      description:
        "Using state-of-the-art equipment and proven techniques, we capture stunning visuals that exceed expectations.",
      icon: "Video",
      duration: "1-5 days",
    },
    {
      step: "04",
      title: "Post-Production Magic",
      description:
        "Expert editing, color grading, and sound design transform raw footage into compelling visual narratives.",
      icon: "Edit3",
      duration: "2-4 weeks",
    },
    {
      step: "05",
      title: "Delivery & Support",
      description:
        "We provide multiple formats optimized for your platforms, plus ongoing support for maximum impact.",
      icon: "CheckCircle",
      duration: "1 week",
    },
  ];

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent-foreground px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Workflow" size={16} />
            <span>Our Proven Process</span>
          </div>

          <h2 className="text-fluid-4xl font-display font-bold text-primary mb-6">
            From Concept to
            <span className="text-accent"> Cinematic Reality</span>
          </h2>

          <p className="text-fluid-lg text-muted-foreground leading-relaxed">
            Every project follows our refined 5-step process, ensuring
            consistent quality and exceptional results across all service
            categories.
          </p>
        </div>

        <div className="relative">
          {/* Process Timeline */}
          <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-accent via-accent/50 to-accent"></div>

          <div className="space-y-12 lg:space-y-16">
            {processSteps?.map((step, index) => (
              <div
                key={index}
                className={`flex flex-col lg:flex-row items-center gap-8 ${
                  index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                }`}
              >
                {/* Content */}
                <div
                  className={`flex-1 ${
                    index % 2 === 0
                      ? "lg:text-right lg:pr-12"
                      : "lg:text-left lg:pl-12"
                  }`}
                >
                  <div className="bg-card border border-border rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <div className="flex items-center gap-4 mb-4">
                      <div
                        className={`w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center ${
                          index % 2 === 0 ? "lg:order-2" : "lg:order-1"
                        }`}
                      >
                        <Icon
                          name={step?.icon}
                          size={24}
                          className="text-accent"
                        />
                      </div>
                      <div
                        className={
                          index % 2 === 0 ? "lg:order-1" : "lg:order-2"
                        }
                      >
                        {/* Duration hidden as requested */}
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-primary mb-3 font-display">
                      {step?.title}
                    </h3>

                    <p className="text-muted-foreground leading-relaxed">
                      {step?.description}
                    </p>
                  </div>
                </div>

                {/* Step Number */}
                <div className="relative z-10 flex-shrink-0">
                  <div className="w-16 h-16 bg-accent text-accent-foreground rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                    {step?.step}
                  </div>
                </div>

                {/* Spacer for alignment */}
                <div className="flex-1 hidden lg:block"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Process CTA */}
        <div className="text-center mt-16">
          <div className="bg-card border border-border rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-primary mb-4 font-display">
              Ready to Start Your Project?
            </h3>
            <p className="text-muted-foreground mb-6">
              Let's discuss your vision and create something extraordinary
              together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-cta hover:bg-cta/90 text-cta-foreground px-6 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2">
                <Icon name="Calendar" size={18} />
                Schedule Discovery Call
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
